import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { authApi } from '../api.ts';

interface AuthState {
  authenticated: boolean | null; // null = still checking
  exp?: number;
}

interface AuthContextValue extends AuthState {
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ authenticated: null });

  // Check session on mount
  useEffect(() => {
    authApi
      .me()
      .then((res) => setState({ authenticated: res.authenticated, exp: res.exp }))
      .catch(() => setState({ authenticated: false }));
  }, []);

  async function login(password: string): Promise<void> {
    await authApi.login(password);
    const me = await authApi.me();
    setState({ authenticated: me.authenticated, exp: me.exp });
  }

  async function logout(): Promise<void> {
    await authApi.logout();
    setState({ authenticated: false });
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
